import {
  collection,
  doc,
  query,
  serverTimestamp,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  deleteField,
} from 'firebase/firestore'
import type { FirestoreInvitation } from './schema'
import { schema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { DateTime } from 'luxon'
import { UserProfileRole } from '../UserProfile/types'
import { InvitationType, RoomStateDemoType } from '../../types'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import { Invitation } from '../../models/Invitation'
import {
  addDocWithError,
  getDocsWithError,
  getDocWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import type { FieldValue } from 'firebase-admin/firestore'

const converter: FirestoreDataConverter<FirestoreInvitation> = {
  toFirestore: (data) => {
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export async function fetchInvitation(
  repository: FirebaseRepository,
  {
    invitationId,
  }: {
    invitationId: string
  }
) {
  const firestore = repository.firestore
  const invitationRef = doc(
    firestore,
    'invitation',
    invitationId
  ).withConverter(converter)

  const invitationSnap = await getDocWithError(
    invitationRef,
    'FetchInvitationError'
  )
  if (!invitationSnap.exists()) return null

  return convertDocumentSnapshotToModel(repository, invitationSnap, Invitation)
}

export async function createStudentInvitation(
  repository: FirebaseRepository,
  {
    sectionId,
    type,
  }: {
    sectionId: string
    type?: InvitationType
  }
) {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    sectionId: sectionId,
    userId: repository.uid,
    updatedAt: new Date(),
    oneTime: false,
  }

  if (type === InvitationType.multiUse) {
    data.expiresAt = DateTime.now().plus({ month: 3 }).toJSDate()
  } else {
    data.oneTime = true
  }

  return addDocWithError(invitationsRef, data, 'CreateStudentInvitationError')
}

export async function createTaInvitation(
  repository: FirebaseRepository,
  {
    instructorUserId,
    collaboratorRWSectionIds,
  }: {
    instructorUserId?: string
    collaboratorRWSectionIds?: string[]
  }
) {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    role: UserProfileRole.ta,
    oneTime: true,
    userId: instructorUserId || repository.uid,
    updatedAt: new Date(),
  }

  if (collaboratorRWSectionIds) {
    data.collaboratorRWSectionIds = collaboratorRWSectionIds
  }

  return addDocWithError(invitationsRef, data, 'CreateTaInvitationError')
}

export function getAllAssistantInvitations(
  repository: FirebaseRepository,
  { instructorUserId }: { instructorUserId?: string }
) {
  const ref = collection(repository.firestore, 'invitation').withConverter(
    converter
  )

  // This query includes expired invitations (where expiresAt exists and is prior to now) since there's not a good way to exclude them in the query
  const q = query(
    ref,
    where('userId', '==', instructorUserId || repository.uid),
    where('role', '==', UserProfileRole.ta),
    where('oneTime', '==', true)
  )

  return modelListStream(repository, q, Invitation)
}

export async function deleteInvitation(
  repository: FirebaseRepository,
  invitationId: string
) {
  const firestore = repository.firestore
  const invitationRef = doc(firestore, 'invitation', invitationId)

  return updateDocWithError(
    invitationRef,
    {
      expiresAt: serverTimestamp(),
      oneTime: deleteField(),
      updatedAt: serverTimestamp(),
    },
    'DeleteInvitationError'
  )
}

export async function updateInvitationSections(
  repository: FirebaseRepository,
  invitationId: string,
  sectionIds: string[] | null
) {
  const firestore = repository.firestore
  const invitationRef = doc(firestore, 'invitation', invitationId)

  return updateDocWithError(
    invitationRef,
    {
      collaboratorRWSectionIds: sectionIds ? sectionIds : deleteField(),
      updatedAt: serverTimestamp(),
    },
    'UpdateInvitationSectionsError'
  )
}

export async function createSectionInvitation(repository: FirebaseRepository) {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    role: UserProfileRole.ta,
    expiresAt: DateTime.now().plus({ month: 1 }).toJSDate(),
    oneTime: true,
    userId: repository.uid,
    updatedAt: new Date(),
  }

  return addDocWithError(invitationsRef, data, 'CreateSectionInvitationError')
}

export const createSlideDeckReviewInvitation = async (
  repository: FirebaseRepository,
  {
    slideDeckId,
  }: {
    slideDeckId: string
  }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    slideDeckIdToReview: slideDeckId,
    oneTime: true,
    userId: repository.uid,
    updatedAt: new Date(),
  }

  return addDocWithError(
    invitationsRef,
    data,
    'CreateSlideDeckReviewInvitationError'
  )
}

/// create role invitation with catalog
export const createInvitationInstructorWithCatalog = (
  repository: FirebaseRepository,
  {
    catalogId,
    catalogIds,
    oneTime = true,
  }: { catalogId?: string; catalogIds?: string[]; oneTime?: boolean }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    catalogId,
    catalogIds,
    oneTime: true,
    role: UserProfileRole.instructor,
    userId: repository.uid,
    updatedAt: new Date(),
  }
  if (!oneTime) {
    delete data.oneTime
    data.expiresAt = DateTime.now().plus({ month: 1 }).toJSDate()
  }

  return addDocWithError(
    invitationsRef,
    data,
    'CreateInstructorInvitationError'
  )
}

export const createDemoInvitation = (
  repository: FirebaseRepository,
  {
    roomId,
    firstName,
    lastName,
    emailAddress,
    demoType,
  }: {
    roomId: string
    firstName: string
    lastName: string
    emailAddress: string
    demoType?: RoomStateDemoType
  }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const data: Partial<FirestoreInvitation> = {
    oneTime: false,
    userId: repository.uid,
    updatedAt: new Date(),
    isDemo: true,
    demoArguments: {
      firstName: firstName,
      lastName: lastName,
      emailAddress: emailAddress,
      roomId: roomId,
      demoType: demoType || RoomStateDemoType.regular,
    },
    expiresAt: DateTime.now().plus({ month: 1 }).toJSDate(),
  }

  return addDocWithError(invitationsRef, data, 'CreateDemoInvitationError')
}

export const fetchDemoInvitations = async (
  repository: FirebaseRepository,
  {
    roomId,
  }: {
    roomId: string
  }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const predicate = where('demoArguments.roomId', '==', roomId)
  const q = query(invitationsRef, predicate)

  const docs = await getDocsWithError(q, 'FetchDemoInvitationsError')

  return docs.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, Invitation)
  })
}

export const getDemoInvitations = (
  repository: FirebaseRepository,
  {
    roomId,
  }: {
    roomId: string
  }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const predicate = where('demoArguments.roomId', '==', roomId)
  const q = query(invitationsRef, predicate)

  return modelListStream(repository, q, Invitation)
}

/**
 * demo invitations created by the current user for one of their rooms;
 * the userId filter is required for the query to pass rules for
 * non-internal users (searchingForMyInvitations)
 */
export const getMyDemoInvitations = (
  repository: FirebaseRepository,
  {
    roomId,
  }: {
    roomId: string
  }
) => {
  const firestore = repository.firestore
  const invitationsRef = collection(firestore, 'invitation').withConverter(
    converter
  )

  const q = query(
    invitationsRef,
    where('userId', '==', repository.uid),
    where('demoArguments.roomId', '==', roomId)
  )

  return modelListStream(repository, q, Invitation)
}

export const isSectionInvitation = async (
  repository: FirebaseRepository,
  { invitationId }: { invitationId: string }
) => {
  const colRef = collection(repository.firestore, 'invitation').withConverter(
    converter
  )
  const docRef = doc(colRef, invitationId)
  const invitation = await getDocWithError(docRef, 'FetchInvitationError')
  const exists = invitation.exists()
  if (!exists) return false
  if (invitation.data().sectionId) return true
  return false
}

export const createOrgInvitation = async (
  repository: FirebaseRepository,
  args: {
    organizationId: string
    organizationAdmin: boolean
  }
) => {
  const colRef = collection(repository.firestore, 'invitation').withConverter(
    converter
  )
  const invitation: Omit<FirestoreInvitation, 'updatedAt'> & {
    updatedAt: FieldValue
  } = {
    oneTime: true,
    userId: repository.uid,
    updatedAt: serverTimestamp(),
    organizationId: args.organizationId,
    organizationAdmin: args.organizationAdmin,
    role: UserProfileRole.instructor,
  }

  const ref = await addDocWithError(
    colRef,
    invitation,
    'CreateOrganizationInvitationError'
  )
  return ref.id
}
